import { Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2, DOCUMENT } from '@angular/core';
import { ActivatedRoute, ActivationEnd, Router, RoutesRecognized } from '@angular/router';
import { MainMenuItemId } from '@sb-events/enums/main-menu-item';
import { AvatarButtonAction } from '@sb-shared/constants/shared.constant';
import { Organisation } from '@sb-shared/models/organisation';
import { Button } from '@sb-shared/models/UI/buttons';
import { MainMenuItem, MenuItem } from '@sb-shared/models/UI/menu-item';
import { UserUi } from '@sb-shared/models/user-ui';
import { DeviceService } from '@sb-shared/services/device.service';
import { LocaleService } from '@sb-shared/services/locale.service';
import { MenuService } from '@sb-shared/services/menu.service';
import { OrganisationService } from '@sb-shared/services/organisation.service';
import { UserService } from '@sb-shared/services/user.service';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { filter, map, pairwise, share } from 'rxjs/operators';
import { Ng1Translate } from 'src/app/ng1-services/ng1-services.module';
import { AuthService } from '../../../auth/services/auth.service';
import { DocTitleService } from './../../services/doc-title.service';
import { UnreadMessageCountBridgeService } from 'src/app/messaging/components/messaging/unread-message-count-bridge.service';
import { environment } from 'src/environments/environment';

const sideMenuOpenCookieName = 'sideMenuOpen';
@Component({
    selector: 'sb-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss'],
    standalone: false
})
export class ShellComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  totalMenuBadgeCount$: Observable<number>;
  unreadMsgCount$: Observable<number>;
  currentUser$: Observable<UserUi>;
  currentOrganisation: Organisation;
  mobileMenuIsCollapsed = true;
  scriptDirection$: Observable<string> = null;
  isSideMenuOpen = false;
  isPageSideMenuOpen = false;
  translationsReady = false;
  moduleClass: string;
  avatarDropdownItems: Button[];
  rightMenuItems: MainMenuItem[];
  currentMainMenuItem: MainMenuItem;
  isLoadingPage = true;
  isMobileApp: boolean;
  mainMenuItemId = MainMenuItemId;
  readonly AvatarButtonAction = AvatarButtonAction;
  cdnBase: string = environment.cdnBase;
  private settingsRoute: string;
  private isOnSettings = false;
  private isSideMenuOpenBeforeSettings = false;

  constructor(
    private menu: MenuService,
    private user: UserService,
    private organisation: OrganisationService,
    private locale: LocaleService,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private ng1Translate: Ng1Translate,
    private renderer: Renderer2,
    private cookie: CookieService,
    private device: DeviceService,
    private docTitle: DocTitleService,
    private location: Location,
    private unreadMessageCountBridge: UnreadMessageCountBridgeService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.unreadMsgCount$ = this.menu.getMainMenuItems()
      .pipe(map((menuItems) => menuItems.find(item => item.menuItemTypeId === MainMenuItemId.Messaging)?.count))
    this.totalMenuBadgeCount$ = this.menu.getMainMenuItems()
      .pipe(map((menuItems) => menuItems.filter(item => [MainMenuItemId.Messaging, MainMenuItemId.Diary].some(id => item.menuItemTypeId == id)).reduce((acc, item) => acc + item.count, 0)))
    this.currentUser$ = this.user.getCurrentUser().pipe(share());

    this.unreadMessageCountBridge.unreadMessageCount$.subscribe(count => {
      const messagingMenuItem = this.rightMenuItems?.find(item => item.menuItemTypeId === MainMenuItemId.Messaging);
      if (messagingMenuItem) {
        messagingMenuItem.count = count;
        this.menu.clearPersonUnreadCountCache();
      }
    });
  }

  ngOnInit(): void {
    // Handle mobile app header
    this.route.queryParams.subscribe(params => {
      if (params.logOut) {
        this.logOut();
      }
      if (params.isMobileApp) {
        this.isMobileApp = true;
        this.renderer.addClass(this.document.body, 'mobile-app');
      }
      if (params.isModalOnly) {
        this.renderer.addClass(this.document.body, 'modal-only');
      }
      if (params.showModalType) {
        this.renderer.addClass(this.document.body, 'modal-only');
        this.renderer.addClass(this.document.body, `modal-type-${params.showModalType}`);
      }

      if (params.hideSubMenu) {
        this.menu.setShowSubMenu(false);
      }
    });

    this.ng1Translate.onReady(() => {
      this.docTitle.initialise();

      this.subscriptions.add(forkJoin([
        this.organisation.getCurrentOrganisation(),
        this.menu.getMainMenuItems(),
        this.menu.getAvatarMenuItems(),
      ])
        .subscribe(([
          organisation,
          mainMenuItems,
          avatarMenuItems,
        ]: [
            Organisation,
            MainMenuItem[],
            MenuItem[],
          ]) => {
          this.currentOrganisation = organisation;
          this.settingsRoute = mainMenuItems
            .find(menuItem => menuItem.menuItemTypeId === MainMenuItemId.Settings)?.route;
          this.rightMenuItems = mainMenuItems
            .filter(menuItem => menuItem.isRightMenu)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(menuItem => menuItem.children?.length
              ? { ...menuItem, dropdownItems: this.toDropdownItems(menuItem.children) }
              : menuItem);

          this.avatarDropdownItems = mainMenuItems
            .filter(menuItem => menuItem.isAvatarMenu)
            .map(menuItem => <MenuItem>menuItem)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .concat(avatarMenuItems)
            .map(menuItem => <Button>({
              ...menuItem,
              message: menuItem.label
            }));
          this.scriptDirection$ = this.locale.getScriptDirection();

          this.subscriptions.add(this.menu.currentMainMenuItem.subscribe(mainMenuItem => {
            this.currentMainMenuItem = mainMenuItem;
            this.isPageSideMenuOpen = mainMenuItem?.isAlwaysOpen;
            this.isLoadingPage = false;
          }));

          this.subscriptions.add(this.router.events.pipe(filter(e => (e instanceof ActivationEnd)))
            .subscribe(() => this.setSideMenuStateForRoute(
              this.router.getCurrentNavigation()?.finalUrl?.toString() ?? this.router.url))
          );

          // The menu items are only available now, so apply the state for the page already loaded
          this.setSideMenuStateForRoute(this.router.url);
        })
      );
    });

    this.startMobileAppQueryPropagation();

    // Get menu expanded state
    this.isSideMenuOpen = this.device.isXxlDevice() && this.cookie.get(sideMenuOpenCookieName) === '1';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe;
    this.docTitle.destroy();
  }

  logOut() {
    this.auth.logout();
  }

  private toDropdownItems(children: MenuItem[]): Button[] {
    return children
      .filter(child => child.externalUrl)
      .map(child => ({
        message: child.label,
        externalUrl: child.externalUrl,
        openInSeparateTab: child.openInSeparateTab
      }));
  }

  toggleSideMenu() {
    this.isSideMenuOpen = !this.isSideMenuOpen;
    this.cookie.set(sideMenuOpenCookieName, this.isSideMenuOpen ? '1' : '0');
  }

  /**
   * Some Settings menu items intentionally have no icon, so they show as an empty gap while the
   * side menu is collapsed. Keep the menu expanded throughout Settings, and put it back how it was
   * on the way out. The stored preference (cookie) is deliberately left untouched, as the user
   * didn't choose to expand the menu.
   */
  private setSideMenuStateForRoute(url: string) {
    const isOnSettings = !!this.settingsRoute && url?.indexOf(this.settingsRoute) === 1;

    if (isOnSettings) {
      if (!this.isOnSettings) {
        this.isSideMenuOpenBeforeSettings = this.isSideMenuOpen;
      }
      this.isSideMenuOpen = true;
    } else if (this.isOnSettings) {
      // Leaving Settings - restore the previous state (never open on devices that close on navigation)
      this.isSideMenuOpen = this.isSideMenuOpenBeforeSettings && this.device.isXxlDevice();
    } else if (!this.device.isXxlDevice()) {
      // Close menu when navigating unless XXL device
      this.isSideMenuOpen = false;
    }

    this.isOnSettings = isOnSettings;
  }

  onClickContent() {
    // Close sidemenu when clicking on the main page (unless xxl device with room for both)
    if (!this.device.isXxlDevice()) {
      this.isSideMenuOpen = false;
    }
  }

  isSvgIcon(item: MenuItem): boolean {
    return item.iconImageUrl?.endsWith('.svg') ?? false;
  }


  /**
   * Each time the route is changed this checks the current URL query string to see if configured for mobile app
   * and if the destination URL is missing the required query it will append it.
   */
  private startMobileAppQueryPropagation() {
    this.router.events
      .pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise())
      .subscribe((events: RoutesRecognized[]) => {
        // the current URL cannot be retrieved from the events for this, as it will not always include the query string
        const currentUrl = this.location.path();
        const newUrl = events[1].urlAfterRedirects;
        const mobileAppMarker = 'isMobileApp=true';

        if (currentUrl.includes(mobileAppMarker) && !newUrl.includes(mobileAppMarker)) {
          setTimeout(() => {
            this.location.replaceState(this.location.path(), mobileAppMarker)
          })
        }
    });
  }
}
